/**
 * Generate stable, unique CSS selectors for DOM elements.
 * Priority: data-testid > data-qa/data-cy > data-demo-id > id > name >
 * aria-label > role+text > computed path. Utility-class chains
 * (Tailwind, MUI sx, etc.) are NEVER used — they snap on theme tweaks.
 */

const TESTING_DATA_ATTRS = [
  'data-testid',  // RTL / Cypress / Playwright canonical
  'data-test',    // Older test-only convention
  'data-qa',      // QA team convention
  'data-cy',      // Cypress-specific
  'data-demo-id', // DemoAgent custom
];

export function generateSelector(element: Element): string {
  // Priority 1-3: testing data-attrs in preference order. Mining all of
  // them lifts selector quality dramatically on apps that don't use
  // data-testid but DO use cypress/qa equivalents. P1 (#19).
  for (const attr of TESTING_DATA_ATTRS) {
    const v = element.getAttribute(attr);
    if (v) return `[${attr}="${cssEscapeAttr(v)}"]`;
  }

  // Priority 3: Unique ID
  if (element.id && isUniqueId(element)) {
    return `#${CSS.escape(element.id)}`;
  }

  // Priority 4: name attribute (form inputs)
  const name = element.getAttribute('name');
  if (name) {
    const selector = `[name="${CSS.escape(name)}"]`;
    if (document.querySelectorAll(selector).length === 1) return selector;
  }

  // Priority 5: aria-label
  const ariaLabel = element.getAttribute('aria-label');
  if (ariaLabel) {
    const selector = `[aria-label="${CSS.escape(ariaLabel)}"]`;
    if (document.querySelectorAll(selector).length === 1) return selector;
  }

  // Priority 6: role + accessible name
  const role = element.getAttribute('role');
  if (role) {
    const text = (element as HTMLElement).innerText?.trim().slice(0, 30);
    if (text) {
      // Try role + text content
      const tag = element.tagName.toLowerCase();
      const candidates = document.querySelectorAll(`${tag}[role="${role}"]`);
      for (let i = 0; i < candidates.length; i++) {
        if (candidates[i] === element) {
          if (candidates.length === 1) return `${tag}[role="${role}"]`;
          return `${tag}[role="${role}"]:nth-of-type(${i + 1})`;
        }
      }
    }
  }

  // Priority 7: Computed path (fallback)
  return computePath(element);
}

/** Escape a string for safe inclusion in a CSS attribute selector value. */
function cssEscapeAttr(value: string): string {
  return value.replace(/(["\\])/g, '\\$1');
}

/**
 * Heuristic: is this class likely a Tailwind/MUI utility class? Utility
 * classes change on every theme tweak, so a selector that depends on
 * them snaps. We scrub them out of selectors when we have no choice but
 * to use class.
 */
const UTILITY_CLASS_PATTERNS: RegExp[] = [
  /^(?:p|m|px|py|pt|pb|pl|pr|mx|my|mt|mb|ml|mr)-(?:\d+|auto|px|\[.+\])$/,
  /^(?:w|h|min-w|min-h|max-w|max-h)-(?:\d+|full|screen|fit|min|max|auto|\[.+\])$/,
  /^text-(?:xs|sm|base|lg|xl|2xl|3xl|4xl|5xl|6xl|left|right|center|justify|white|black|gray|.+)$/,
  /^(?:bg|border|ring|outline|shadow|rounded|opacity|cursor|hover:|focus:|active:|sm:|md:|lg:|xl:)/,
  /^(?:flex|grid|inline|block|hidden|relative|absolute|fixed|sticky|static)$/,
  /^[a-z]+:(?:.+)$/,  // any responsive / state prefix
];

export function isUtilityClass(cls: string): boolean {
  return UTILITY_CLASS_PATTERNS.some((re) => re.test(cls));
}

function isUniqueId(element: Element): boolean {
  if (!element.id) return false;
  // Avoid auto-generated IDs (React, Angular often generate these)
  if (/^:r[0-9a-z]+:$/.test(element.id)) return false;
  if (/^(react|ng|vue|_|__)-/.test(element.id)) return false;
  return document.querySelectorAll(`#${CSS.escape(element.id)}`).length === 1;
}

function computePath(element: Element): string {
  const parts: string[] = [];
  let current: Element | null = element;

  while (current && current !== document.body) {
    const tag = current.tagName.toLowerCase();
    const parent: Element | null = current.parentElement;

    if (parent) {
      const currentTag = current.tagName;
      const siblings: Element[] = Array.from(parent.children).filter(
        (s: Element) => s.tagName === currentTag,
      );
      if (siblings.length > 1) {
        const index = siblings.indexOf(current) + 1;
        parts.unshift(`${tag}:nth-of-type(${index})`);
      } else {
        parts.unshift(tag);
      }
    } else {
      parts.unshift(tag);
    }

    current = parent;
    if (parts.length >= 4) break;
  }

  return parts.join(' > ');
}
